/**
 * Every correction, as a list of events to append.
 *
 * The headline feature, and the thing incumbents are most disliked for: an over recorded
 * against the wrong bowler, fixed on the device, offline, with wickets and economy and
 * maidens correcting themselves. None of that arithmetic is here, and that is the point —
 * every function returns *events*, and the numbers come back out of `replay()`. A correction
 * that adjusted a total would be the hand-patching this whole design exists to avoid.
 *
 * Three rules hold throughout:
 *
 * - **Nothing is deleted or rewritten.** A correction is a new event carrying `supersedes`,
 *   and voiding is a new event carrying `voided`. The original stays in the ledger exactly as
 *   it was appended, which is what makes the audit trail real rather than claimed.
 * - **A correction lands at the address of the ball it replaces**, not at its own, so
 *   everything downstream re-derives (ADR-0004). That is the engine's job; all that is
 *   required here is to name the right `supersedes`.
 * - **Multi-event corrections are one list, threaded.** Reassigning an over is seven events,
 *   and each needs its own address — so the draft is committed as they are built, exactly as
 *   `undoLastBall` does. Returning them as one list also means `record()` stores them
 *   together or not at all: half a reassigned over is a scorecard nobody chose.
 *
 * Pure, and in a `.ts` file with no JSX, because every function here is a decision about what
 * the ledger should say and belongs under test rather than under a renderer.
 */
import { isDelivery } from "@sportsos/engine";
import type {
  BatterHand,
  BatterIn,
  BowlerChange,
  BowlingArm,
  BowlingType,
  Correction,
  Delivery,
  Event,
  EventId,
  InningsNo,
  MatchState,
  NewOver,
  PlayerId,
  Runs,
  Shot,
  Wicket,
} from "@sportsos/engine";
import { buildRuns, type Latch } from "./entry.ts";
import { build, commit, liveEvents, voidingCopy, type MatchDraft } from "./draft.ts";

/** The live version of one event, by the id a chip carries. */
export function findLive(draft: MatchDraft, eventId: EventId): Event | undefined {
  return liveEvents(draft).find((event) => event.eventId === eventId);
}

/** The live delivery a chip points at, or undefined if it has since been voided. */
export function findDelivery(draft: MatchDraft, eventId: EventId): Delivery | undefined {
  const event = findLive(draft, eventId);
  return event !== undefined && isDelivery(event) ? event : undefined;
}

/**
 * Build the events for a batch of corrections, each at its own address.
 *
 * The threading is the whole helper: `build` takes the next free `seq` from the draft it is
 * given, so building seven events against one unchanged draft would give all seven the same
 * address — six lost balls, and a `seq_conflict` storm when the outbox drained.
 */
function batch(
  draft: MatchDraft,
  steps: readonly ((working: MatchDraft) => Event | null)[],
): readonly Event[] {
  const events: Event[] = [];
  let working = draft;
  for (const step of steps) {
    const event = step(working);
    if (event === null) continue;
    events.push(event);
    working = commit(working, event);
  }
  return events;
}

/**
 * A superseding copy of a delivery with some fields changed.
 *
 * The whole original is handed to `build`, ledger fields included, because `build` overwrites
 * every one of them — a fresh `eventId`, the next free `seq`, this scorer's provenance and
 * now. Stripping them here first would be eight lines saying what `build` already guarantees,
 * and would break silently if a ninth ledger field were ever added.
 */
function correctedDelivery(
  draft: MatchDraft,
  original: Delivery,
  changes: Partial<Delivery>,
): Delivery {
  return build<Delivery>(
    draft,
    original.inningsNo,
    { ...original, ...changes },
    { supersedes: original.eventId },
  );
}

// ── One ball ────────────────────────────────────────────────────────────────

/**
 * Void a ball entirely — a dead ball recorded in error.
 *
 * Not a deletion: the ball stays in the ledger and is skipped during replay, so the over
 * renumbers itself and the address it occupied stays taken (CONTEXT.md, "Voided").
 *
 * Unlike undo, nothing that followed is touched. Undo reaches back through the trailing
 * bookkeeping because it is unwinding the *last* thing that happened; voiding ball 3 of over 7
 * is a statement about that ball alone, and taking the next batter with it would invent a
 * second correction the scorer did not ask for.
 */
export function voidBall(draft: MatchDraft, eventId: EventId): readonly Event[] {
  const original = findLive(draft, eventId);
  if (original === undefined) return [];
  return [voidingCopy(draft, original)];
}

/** What an edit sheet can change about a ball. */
export interface BallEdit {
  readonly latch: Latch;
  readonly runs: number;
  readonly wicket: Wicket | null;
}

/**
 * Re-record one ball.
 *
 * `overthrow` and `shortRun` are carried across from the original rather than rebuilt, because
 * the pad cannot express either — so a ball that had a short run deducted would silently lose
 * the deduction on any edit, and the total would move for a reason nothing on screen explains.
 */
export function editBall(
  draft: MatchDraft,
  eventId: EventId,
  edit: BallEdit,
): readonly Event[] {
  const original = findDelivery(draft, eventId);
  if (original === undefined) return [];

  const rebuilt = buildRuns(edit.latch, edit.runs);
  const runs: Runs = {
    ...rebuilt,
    overthrow: original.runs.overthrow,
    shortRun: original.runs.shortRun,
    total: rebuilt.total - original.runs.shortRun,
  };

  return [
    correctedDelivery(draft, original, {
      runs,
      wicket: edit.wicket,
      legal: rebuilt.extras.wide === 0 && rebuilt.extras.noball === 0,
    }),
  ];
}

/**
 * Say where a ball went, or that it was not recorded, without touching anything else (ADR-0022).
 *
 * A correcting delivery that copies the original and changes only `shot`: the runs, the strike
 * and the wicket are the original's, so the score is the same before and after and the ball is
 * only marked "edited" because it was. Passing `null` removes a direction entered by mistake.
 */
export function redirectBall(
  draft: MatchDraft,
  eventId: EventId,
  shot: Shot | null,
): readonly Event[] {
  const original = findDelivery(draft, eventId);
  if (original === undefined) return [];
  return [correctedDelivery(draft, original, { shot })];
}

/**
 * The batters were at the other ends, from this ball on.
 *
 * Two taps, and it works because a *correcting* delivery's recorded pair is authoritative
 * while an original's is advisory (ADR-0004, amended by #15). Everything after re-derives from
 * the new anchor, so one event fixes the rest of the innings.
 *
 * The alternative was to make the scorer find whichever earlier ball had its crossings
 * mis-recorded, which is not something anyone can do on a ground.
 */
export function swapEndsFrom(draft: MatchDraft, eventId: EventId): readonly Event[] {
  const original = findDelivery(draft, eventId);
  if (original === undefined) return [];
  return [
    correctedDelivery(draft, original, {
      strikerId: original.nonStrikerId,
      nonStrikerId: original.strikerId,
    }),
  ];
}

// ── A whole over ────────────────────────────────────────────────────────────

/**
 * Reassign an over to a different bowler.
 *
 * One superseding `NewOver` plus one superseding `Delivery` per ball — the single mechanism,
 * rather than a bespoke "reassign" event (CONTEXT.md: "One mechanism is worth more than saving
 * rows"). Wickets, runs, economy, maidens and spells all move because they were never stored
 * in the first place; they are `replay()` of these events.
 *
 * The `NewOver` matters as much as the balls. Leaving it behind would credit the deliveries to
 * the new bowler while the fold still believed the old one had the ball, so he would sit on
 * the card with a phantom `0-0-0-0` and the over count would disagree with the figures.
 */
export function reassignOver(
  draft: MatchDraft,
  state: MatchState,
  inningsNo: InningsNo,
  overNo: number,
  bowlerId: PlayerId,
): readonly Event[] {
  const innings = state.innings.find((entry) => entry.inningsNo === inningsNo);
  if (innings === undefined) return [];

  const ballIds = innings.deliveries
    .filter((chip) => chip.over === overNo)
    .map((chip) => chip.eventId);

  const newOver = liveEvents(draft).find(
    (event): event is NewOver =>
      event.type === "NewOver" && event.inningsNo === inningsNo && event.overNo === overNo,
  );

  return batch(draft, [
    (working) =>
      newOver === undefined
        ? null
        : build<NewOver>(
            working,
            inningsNo,
            { type: "NewOver", overNo, bowlerId, overrideReason: null },
            { supersedes: newOver.eventId },
          ),
    ...ballIds.map(
      (ballId) => (working: MatchDraft) => {
        const original = findDelivery(working, ballId);
        return original === undefined ? null : correctedDelivery(working, original, { bowlerId });
      },
    ),
  ]);
}

// ── How someone bats and bowls ──────────────────────────────────────────────

/**
 * Correct which way a batter stands, from the moment he came in (ADR-0023).
 *
 * One superseding `BatterIn` for each time he walked in this innings (a batter who retired hurt
 * and came back has two). Because a correction takes effect at the address of the event it
 * replaces (ADR-0004), every ball he faced is read with the new hand and every shot he placed is
 * redrawn on the other side of the ground. The score is untouched, and so is where any ball went:
 * that is why the hand is not copied onto each shot, where it would have taken one correction per
 * shot to change and an edited mark on each.
 *
 * Nothing for a walk-in that already says this, so choosing the hand he already has is a no-op.
 */
export function correctBattingHand(
  draft: MatchDraft,
  inningsNo: InningsNo,
  playerId: PlayerId,
  hand: BatterHand,
): readonly Event[] {
  const walkIns = liveEvents(draft).filter(
    (event): event is BatterIn =>
      event.type === "BatterIn" && event.inningsNo === inningsNo && event.playerId === playerId,
  );
  return batch(
    draft,
    walkIns.map((original) => (working: MatchDraft) =>
      original.battingHand === hand
        ? null
        : build<BatterIn>(
            working,
            inningsNo,
            { ...original, battingHand: hand },
            { supersedes: original.eventId },
          ),
    ),
  );
}

/** Every live event that put this bowler on in one innings: a new over, or a change mid-over. */
const bowlerStarts = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  bowlerId: PlayerId,
): readonly (NewOver | BowlerChange)[] =>
  liveEvents(draft).filter(
    (event): event is NewOver | BowlerChange =>
      (event.type === "NewOver" || event.type === "BowlerChange") &&
      event.inningsNo === inningsNo &&
      event.bowlerId === bowlerId,
  );

type Step = (working: MatchDraft) => Event | null;

/** One superseding event for each time this bowler was put on, unless it already says `arm`. */
const armSteps = (
  draft: MatchDraft,
  inningsNo: InningsNo,
  bowlerId: PlayerId,
  arm: BowlingArm,
): readonly Step[] =>
  bowlerStarts(draft, inningsNo, bowlerId).map((original) => (working: MatchDraft) =>
    original.bowlingArm === arm
      ? null
      : original.type === "NewOver"
        ? build<NewOver>(working, inningsNo, { ...original, bowlingArm: arm }, { supersedes: original.eventId })
        : build<BowlerChange>(working, inningsNo, { ...original, bowlingArm: arm }, { supersedes: original.eventId }),
  );

/**
 * Looked up when the step runs, not when it is planned: if the arm correction above it has just
 * superseded this over's `NewOver`, the type must replace that newer version and not fork off
 * the old one.
 */
const overTypeStep =
  (inningsNo: InningsNo, overNo: number, type: BowlingType): Step =>
  (working) => {
    const original = liveEvents(working).find(
      (event): event is NewOver =>
        event.type === "NewOver" && event.inningsNo === inningsNo && event.overNo === overNo,
    );
    if (original === undefined || original.bowlingType === type) return null;
    return build<NewOver>(working, inningsNo, { ...original, bowlingType: type }, { supersedes: original.eventId });
  };

/**
 * Correct the arm a bowler bowls with, for the whole innings (ADR-0023).
 *
 * An arm does not change between overs, so a wrong one is a mistake that touches every over he
 * bowled: one superseding event for each. The type is left exactly as each over recorded it.
 */
export function correctBowlingArm(
  draft: MatchDraft,
  inningsNo: InningsNo,
  bowlerId: PlayerId,
  arm: BowlingArm,
): readonly Event[] {
  return batch(draft, armSteps(draft, inningsNo, bowlerId, arm));
}

/**
 * Correct what a bowler bowled in **one over**, because it was entered wrongly.
 *
 * Deliberately not "he changed his style": a real change is just the value on the next over's
 * `NewOver`, and nothing earlier moves. This is the other case, where the over recorded as medium
 * was spin all along, and it rewrites that over only.
 */
export function correctOverType(
  draft: MatchDraft,
  inningsNo: InningsNo,
  overNo: number,
  type: BowlingType,
): readonly Event[] {
  return batch(draft, [overTypeStep(inningsNo, overNo, type)]);
}

/**
 * Both at once, from the edit sheet's "how does he bowl?" step: a new arm for the whole innings
 * and a new type for the one over the scorer tapped. One list, so `record()` stores the arm and
 * the type together or not at all; either may be left out.
 */
export function correctBowlingStyle(
  draft: MatchDraft,
  inningsNo: InningsNo,
  overNo: number,
  bowlerId: PlayerId,
  change: { readonly arm?: BowlingArm; readonly type?: BowlingType },
): readonly Event[] {
  return batch(draft, [
    ...(change.arm === undefined ? [] : armSteps(draft, inningsNo, bowlerId, change.arm)),
    ...(change.type === undefined ? [] : [overTypeStep(inningsNo, overNo, change.type)]),
  ]);
}

// ── Across the innings ──────────────────────────────────────────────────────

/**
 * The address a substitution has to start at to actually take effect.
 *
 * Not the ball the scorer tapped, and this is the constraint that makes the difference between
 * a substitution working and silently doing nothing. Strike and the current bowler are both
 * *derived* — from `BatterIn.takesStrike` and from `NewOver` — so a substitution that began
 * after the event which *named* the player would rewrite the deliveries and leave the fold
 * still crediting the old name.
 *
 * So it snaps back to the latest such event at or before the chosen point. Latest, not
 * earliest: a player who came in, was dismissed and whose namesake came in later should only
 * have the current spell renamed.
 */
export function substitutionFromSeq(
  draft: MatchDraft,
  inningsNo: InningsNo,
  playerId: PlayerId,
  chosenSeq: number,
): number {
  const introductions = liveEvents(draft).filter((event) => {
    if (event.inningsNo !== inningsNo || event.seq > chosenSeq) return false;
    if (event.type === "BatterIn") return event.playerId === playerId;
    if (event.type === "NewOver" || event.type === "BowlerChange") {
      return event.bowlerId === playerId;
    }
    return false;
  });

  // Nothing named him at or before the chosen point, so the chosen point is the honest
  // answer — the deliveries from there on are all there is to rename.
  return introductions.length === 0
    ? chosenSeq
    : Math.max(...introductions.map((event) => event.seq));
}

/**
 * "This was actually a different player", applied from a chosen point.
 *
 * The one amendment that cannot be a superseding event, because it spans events rather than
 * replacing one — which is the entire reason the `Correction` type exists (CONTEXT.md). Ten
 * deliveries, a `BatterIn` and two `NewOver`s might all name the wrong man; ten superseding
 * events would say the same thing ten times and leave the eleventh to be found by hand.
 */
export function substitutePlayer(
  draft: MatchDraft,
  inningsNo: InningsNo,
  input: {
    readonly fromPlayerId: PlayerId;
    readonly toPlayerId: PlayerId;
    readonly chosenSeq: number;
    readonly reason: string;
  },
): readonly Event[] {
  if (input.fromPlayerId === input.toPlayerId) return [];
  const actorId = draft.setup.scorerId;
  if (actorId === null) return [];

  return [
    build<Correction>(draft, inningsNo, {
      type: "Correction",
      reason: input.reason,
      actorId,
      substitution: {
        fromPlayerId: input.fromPlayerId,
        toPlayerId: input.toPlayerId,
        fromSeq: substitutionFromSeq(draft, inningsNo, input.fromPlayerId, input.chosenSeq),
      },
    }),
  ];
}

// ── Undo ────────────────────────────────────────────────────────────────────

/**
 * Undo the last `count` balls.
 *
 * Built as one list rather than by calling `undoLastBall` in a loop, because that function
 * takes a draft and the caller has no way to commit between calls — so all N voids would claim
 * the same address and N-1 balls would be lost to a `seq_conflict`. The draft is threaded here
 * instead.
 *
 * Each step reaches back through the trailing bookkeeping the same way one-ball undo does, so
 * undoing three balls that included a wicket also takes the batter who came in after it.
 */
export function undoLastBalls(draft: MatchDraft, count: number): readonly Event[] {
  const events: Event[] = [];
  let working = draft;

  for (let done = 0; done < count; done += 1) {
    const live = liveEvents(working);
    const lastBall = [...live].reverse().find(isDelivery);
    if (lastBall === undefined) break;

    for (const event of live.slice(live.indexOf(lastBall))) {
      const voiding = voidingCopy(working, event);
      events.push(voiding);
      working = commit(working, voiding);
    }
  }

  return events;
}

/** How many balls are actually there to undo. What the scrub readout is bounded by. */
export function undoableBalls(draft: MatchDraft): number {
  return liveEvents(draft).filter(isDelivery).length;
}
